import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";
import { CONTACT_GROUPS, emptyContact } from "./helpers";

/**
 * ContactForm — modal for creating/editing a contact, including the photo crop modal.
 * Owns its own form state, derived from the `initial` prop when opened.
 *
 * Props:
 *   open: boolean
 *   initial: contact object or null (for new contact)
 *   onClose: () => void
 *   onSaved: () => void   // called after successful create/update
 */
export default function ContactForm({ open, initial, onClose, onSaved }) {
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [form, setForm] = useState(emptyContact());

  // Photo crop state
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const isEdit = !!initial;

  // Reset form whenever modal re-opens
  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      const photos = (initial.attachments || [])
        .filter((a) => a.type === "photo" && a.url)
        .map((a) => ({ id: a.id || "", url: a.url, thumb: a.thumb || "" }));
      if (!photos.length && initial.photo_url) photos.push({ id: "", url: initial.photo_url });
      setForm({
        ...emptyContact(),
        ...initial,
        photos,
        emails: initial.emails?.length ? initial.emails : [{ type: "work", email: "" }],
        phones: initial.phones?.length ? initial.phones : [{ type: "mobile", number: "" }],
        addresses: initial.addresses?.length ? initial.addresses : [{ type: "home", street: "", city: "", state: "", zip: "", country: "" }],
        social: { twitter: "", linkedin: "", facebook: "", instagram: "", ...initial.social },
        relationships: { spouse: "", partner: "", ...initial.relationships },
      });
    } else {
      setForm(emptyContact());
    }
  }, [open, initial]);

  const getCroppedBlob = (imageSrc, pixelCrop) =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
      };
      img.src = imageSrc;
    });

  const handleCropConfirm = async () => {
    if (!cropImage || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedBlob(cropImage, croppedAreaPixels);
      const formData = new FormData();
      formData.append("file", blob, "photo.jpg");
      const res = await axios.post(`${API}/crm/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const photoUrl = `${API}/crm/files/${res.data.id}`;
      const thumbUrl = `${API}/crm/files/${res.data.id}/thumb`;
      const photos = [...(form.photos || []), { id: res.data.id, url: photoUrl, thumb: thumbUrl }];
      setForm((prev) => ({ ...prev, photos, photo_url: photoUrl }));
      toast.success("Photo cropped & uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removePhoto = async (index) => {
    const photos = [...(form.photos || [])];
    const removed = photos[index];
    if (removed?.id) {
      try { await axios.delete(`${API}/crm/files/${removed.id}`); } catch { /* ignore */ }
    }
    photos.splice(index, 1);
    setForm({ ...form, photos, photo_url: photos[0]?.url || "" });
  };

  const replacePhoto = (index) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/crm/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const photoUrl = `${API}/crm/files/${res.data.id}`;
        const thumbUrl = `${API}/crm/files/${res.data.id}/thumb`;
        const photos = [...(form.photos || [])];
        if (photos[index]?.id) {
          try { await axios.delete(`${API}/crm/files/${photos[index].id}`); } catch { /* ignore */ }
        }
        photos[index] = { id: res.data.id, url: photoUrl, thumb: thumbUrl };
        setForm({ ...form, photos, photo_url: photos[0]?.url || photoUrl });
        toast.success("Photo replaced");
      } catch {
        toast.error("Replace failed");
      }
    };
    input.click();
  };

  const save = async () => {
    try {
      const data = { ...form };
      if ((data.group === "Custom" || data.group === "Other") && data._customGroupName) {
        data.group = data._customGroupName;
      }
      delete data._customGroupName;
      if (data.photos?.length) {
        data.photo_url = data.photos[0]?.url || data.photos[0] || "";
        data.attachments = data.photos.map((p) => ({ type: "photo", url: p.url || p, thumb: p.thumb || "", id: p.id || "" }));
      }
      delete data.photos;
      data.emails = data.emails.filter((e) => e.email);
      data.phones = data.phones.filter((p) => p.number);
      data.addresses = data.addresses.filter((a) => a.street || a.city);
      if (!data.first_name) { toast.error("First name is required"); return; }
      if (isEdit) {
        await axios.put(`${API}/crm/contacts/${initial.id}`, data);
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/crm/contacts`, data);
        toast.success("Contact created");
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch {
      toast.error("Failed to save contact");
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-testid="contact-form-modal">
        <div className="card-glass p-6 w-full max-w-2xl mb-20" style={{ background: "rgba(0,0,0,0.95)" }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-white">{isEdit ? "Edit Contact" : "New Contact"}</h2>
            <button onClick={onClose} className="text-[#E8E4DC] hover:text-white">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Group + Names */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <select value={["Custom", "Other"].includes(form.group) && form._customGroupName ? form.group : CONTACT_GROUPS.filter((g) => g !== "All").includes(form.group) ? form.group : "Custom"} onChange={(e) => {
                  const val = e.target.value;
                  if (val === "Custom" || val === "Other") setForm({ ...form, group: val, _customGroupName: "" });
                  else setForm({ ...form, group: val, _customGroupName: undefined });
                }} className="input-dark w-full" data-testid="cf-group">
                  {CONTACT_GROUPS.filter((g) => g !== "All").map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                {(form.group === "Custom" || form.group === "Other") && (
                  <input value={form._customGroupName || ""} onChange={(e) => setForm({ ...form, _customGroupName: e.target.value })} className="input-dark w-full mt-2" placeholder="Enter group name..." data-testid="cf-custom-group-name" />
                )}
              </div>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="input-dark" placeholder="First Name *" data-testid="cf-first-name" />
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="input-dark" placeholder="Last Name" data-testid="cf-last-name" />
            </div>
            {/* Photo + Company */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input type="file" ref={photoInputRef} accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="cf-photo-file" />
                <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" data-testid="cf-photo-camera" />
                {form.photos?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {form.photos.map((photo, idx) => (
                        <div key={idx} className="group relative w-16 h-16 rounded-lg overflow-hidden border border-white/10">
                          <img src={photo.thumb || photo.url || photo} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            <button onClick={() => removePhoto(idx)} className="text-red-400 hover:text-red-300 p-0.5" title="Delete" data-testid={`cf-photo-delete-${idx}`}>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                            <button onClick={() => replacePhoto(idx)} className="text-blue-400 hover:text-blue-300 p-0.5" title="Replace" data-testid={`cf-photo-replace-${idx}`}>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => photoInputRef.current?.click()} className="text-[#A3E635] text-sm hover:underline" data-testid="cf-photo-add-more">+ Add More Photos</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => photoInputRef.current?.click()} className="input-dark flex-1 text-center cursor-pointer hover:border-[#A3E635]/50 transition-colors" data-testid="cf-photo">
                      <span className="text-[#E8E4DC]">Photo</span>
                    </button>
                    <button onClick={() => cameraInputRef.current?.click()} className="input-dark px-3 cursor-pointer hover:border-[#A3E635]/50 transition-colors" title="Take Photo" data-testid="cf-photo-camera-btn">
                      <svg className="w-4 h-4 text-[#E8E4DC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><circle cx="12" cy="13" r="3" /></svg>
                    </button>
                  </div>
                )}
              </div>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-dark" placeholder="Company" data-testid="cf-company" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="input-dark" placeholder="Job Title" data-testid="cf-job-title" />
              <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="input-dark" placeholder="Organization" data-testid="cf-org" />
            </div>
            {/* Emails */}
            <div>
              <label className="text-[#E8E4DC] text-xs font-medium mb-1 block">Emails</label>
              {form.emails.map((em, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <select value={em.type} onChange={(e) => { const n = [...form.emails]; n[i].type = e.target.value; setForm({ ...form, emails: n }); }} className="input-dark w-28 text-sm">
                    <option value="work">Work</option><option value="personal">Personal</option><option value="other">Other</option>
                  </select>
                  <input value={em.email} onChange={(e) => { const n = [...form.emails]; n[i].email = e.target.value; setForm({ ...form, emails: n }); }} className="input-dark flex-1" placeholder="email@example.com" data-testid={`cf-email-${i}`} />
                  {form.emails.length > 1 && <button onClick={() => { const n = form.emails.filter((_, j) => j !== i); setForm({ ...form, emails: n }); }} className="text-red-400 hover:text-red-300 px-2">x</button>}
                </div>
              ))}
              <button onClick={() => setForm({ ...form, emails: [...form.emails, { type: "work", email: "" }] })} className="text-[#A3E635] text-sm hover:underline">+ Add Email</button>
            </div>
            {/* Phones */}
            <div>
              <label className="text-[#E8E4DC] text-xs font-medium mb-1 block">Phone Numbers</label>
              {form.phones.map((ph, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <select value={ph.type} onChange={(e) => { const n = [...form.phones]; n[i].type = e.target.value; setForm({ ...form, phones: n }); }} className="input-dark w-28 text-sm">
                    <option value="mobile">Mobile</option><option value="work">Work</option><option value="home">Home</option><option value="fax">Fax</option>
                  </select>
                  <input value={ph.number} onChange={(e) => { const n = [...form.phones]; n[i].number = e.target.value; setForm({ ...form, phones: n }); }} className="input-dark flex-1" placeholder="Phone number" data-testid={`cf-phone-${i}`} />
                  {form.phones.length > 1 && <button onClick={() => { const n = form.phones.filter((_, j) => j !== i); setForm({ ...form, phones: n }); }} className="text-red-400 hover:text-red-300 px-2">x</button>}
                </div>
              ))}
              <button onClick={() => setForm({ ...form, phones: [...form.phones, { type: "mobile", number: "" }] })} className="text-[#A3E635] text-sm hover:underline">+ Add Phone</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-dark" placeholder="Website" data-testid="cf-website" />
              <input type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="input-dark" data-testid="cf-birthdate" />
              <input value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} className="input-dark" placeholder="Education" data-testid="cf-education" />
            </div>
            {/* Social */}
            <div>
              <label className="text-[#E8E4DC] text-xs font-medium mb-1 block">Social Networking</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.keys(form.social).map((k) => (
                  <input key={k} value={form.social[k]} onChange={(e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } })} className="input-dark" placeholder={k.charAt(0).toUpperCase() + k.slice(1)} data-testid={`cf-social-${k}`} />
                ))}
              </div>
            </div>
            {/* Relationships */}
            <div>
              <label className="text-[#E8E4DC] text-xs font-medium mb-1 block">Relationships</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.relationships.spouse} onChange={(e) => setForm({ ...form, relationships: { ...form.relationships, spouse: e.target.value } })} className="input-dark" placeholder="Spouse" data-testid="cf-spouse" />
                <input value={form.relationships.partner} onChange={(e) => setForm({ ...form, relationships: { ...form.relationships, partner: e.target.value } })} className="input-dark" placeholder="Partner" data-testid="cf-partner" />
              </div>
            </div>
            {/* Address */}
            <div>
              <label className="text-[#E8E4DC] text-xs font-medium mb-1 block">Address</label>
              {form.addresses.map((addr, i) => (
                <div key={i} className="space-y-2 mb-3 p-3 rounded-lg bg-white/5">
                  <input value={addr.street} onChange={(e) => { const n = [...form.addresses]; n[i].street = e.target.value; setForm({ ...form, addresses: n }); }} className="input-dark w-full" placeholder="Street" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <input value={addr.city} onChange={(e) => { const n = [...form.addresses]; n[i].city = e.target.value; setForm({ ...form, addresses: n }); }} className="input-dark" placeholder="City" />
                    <input value={addr.state} onChange={(e) => { const n = [...form.addresses]; n[i].state = e.target.value; setForm({ ...form, addresses: n }); }} className="input-dark" placeholder="State" />
                    <input value={addr.zip} onChange={(e) => { const n = [...form.addresses]; n[i].zip = e.target.value; setForm({ ...form, addresses: n }); }} className="input-dark" placeholder="ZIP" />
                    <input value={addr.country} onChange={(e) => { const n = [...form.addresses]; n[i].country = e.target.value; setForm({ ...form, addresses: n }); }} className="input-dark" placeholder="Country" />
                  </div>
                </div>
              ))}
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark w-full h-24 resize-none" placeholder="Notes..." data-testid="cf-notes" />
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
            <button onClick={onClose} className="btn-secondary flex-1" data-testid="cf-cancel">Cancel</button>
            <button onClick={save} className="btn-primary flex-1" data-testid="cf-save">{isEdit ? "Update Contact" : "Create Contact"}</button>
          </div>
        </div>
      </div>

      {/* Circular Crop Modal */}
      {cropImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm" data-testid="crop-modal">
          <div className="card-glass p-6 w-full max-w-md" style={{ background: "rgba(0,0,0,0.95)" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white">Crop Photo</h2>
              <button onClick={() => { setCropImage(null); setCrop({ x: 0, y: 0 }); setZoom(1); }} className="text-[#E8E4DC] hover:text-white">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="relative w-full" style={{ height: 320 }}>
              <Cropper image={cropImage} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[#E8E4DC] text-xs">Zoom</span>
              <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#A3E635]" data-testid="crop-zoom" />
            </div>
            <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
              <button onClick={() => { setCropImage(null); setCrop({ x: 0, y: 0 }); setZoom(1); }} className="btn-secondary flex-1" data-testid="crop-cancel">Cancel</button>
              <button onClick={handleCropConfirm} className="btn-primary flex-1" data-testid="crop-confirm">Crop & Upload</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
